import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { GridsterConfig, GridType, CompactType, GridsterItem } from 'angular-gridster2';
import { UUID } from 'angular2-uuid';

import { TamDashboardOptions, TamDashboardActions, TamDashboardTileType, TamDashboardEvents } from './tam-dashboard.model';
import { TamDashboardDrillDownConfig } from './tam-dashboard-drill-down/tam-dashboard-drill-down.model';
import { TamDashboardPopUpService, PopUpData } from './tam-dashboard-popup.service';
import * as _ from 'lodash';

@Component({
    selector: 'tam-dashboard',
    templateUrl: './tam-dashboard.component.html',
    styleUrls: ['./tam-dashboard.component.scss']
})
export class TamDashboardComponent implements OnInit, OnDestroy {

    @Input() options: TamDashboardOptions;
    @ViewChild('gridster', { static: false }) gridsterEl: ElementRef;

    gsOptions: GridsterConfig;
    gsItems: any = [];
    actionSubject$: Subject<any> = new BehaviorSubject({});
    feedbackSubject$: Subject<any> = new Subject();
    drillDownConfig: TamDashboardDrillDownConfig = new TamDashboardDrillDownConfig();

    popUpParams: PopUpData = new PopUpData();

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _dashboardPopUpService: TamDashboardPopUpService,
        private _cdr: ChangeDetectorRef,
    ) { }


    ngOnInit() {
        this.gsOptions = {
            fixedRowHeight: 150,
            minCols: 6,
            maxCols: 6,
            gridType: GridType.VerticalFixed,
            compactType: CompactType.CompactUp,
            draggable: {
                enabled: !this.options.readOnly,
                ignoreContentClass: 'gridster-item-content'
            },
            resizable: {
                enabled: !this.options.readOnly
            },
            pushItems: true, // push item when resizing and dragging
            itemChangeCallback: (item) => this._gsItemChangeCallback(item),
            itemInitCallback: (item) => this._gsItemInitCallback(item),
            itemRemovedCallback: (item) => this._gsItemRemovedCallback(item)
        };

        this._destroySubscriptions.push(
            this.options.actionSubject$.subscribe(action => this._onSubscribeActions(action)),
            this.feedbackSubject$.subscribe(event => this._onSubscribeEvents(event)),
            this._dashboardPopUpService.toUpdatePage$.subscribe((data) => {
                this.popUpParams = data;
                this._cdr.detectChanges();
            })
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _getDashboardHeight(items: GridsterItem[]) {
        let height = 0;
        items.forEach(element => {
            if ((element.y + element.rows) > height) {
                height = element.y + element.rows;
            }
        });
        return height;
    }

    private _gsItemChangeCallback(item: GridsterItem) {
        this.options.feedbackSubject$.next({
            type: TamDashboardEvents.ITEM_CHANGED,
            id: item.id,
            payload: {
                id: item.id,
                width: item.cols,
                height: item.rows,
                positionX: item.x,
                positionY: item.y,
                type: item.type,
                name: item.name,
                isLoading: item.isLoading,
                notPreview: !!item.notPreview
            },
        });
    }

    private _gsItemInitCallback(item: GridsterItem) {
        const payload = {
            id: item.id,
            width: item.cols,
            height: item.rows,
            positionX: item.x,
            positionY: item.y,
            type: item.type,
            name: item.name,
            isLoading: item.isLoading,
            notPreview: !!item.notPreview
        };
        if (item.tile) {
            payload['tile'] = item.tile;
        }
        if (item.copyTileId) {
            payload['copyTileId'] = item.copyTileId;
        }
        this.options.feedbackSubject$.next({
            type: TamDashboardEvents.ITEM_CHANGED,
            id: item.id,
            payload: payload
        });
    }

    private _gsItemRemovedCallback(item: GridsterItem) {
        this.options.feedbackSubject$.next({
            type: TamDashboardEvents.ITEM_DELETE,
            id: item.id,
            payload: item
        });
    }

    private _onSubscribeActions(action) {
        if (action.type === TamDashboardActions.RESET) {
            if (action.gridType) {
                this.gsOptions.gridType = action.gridType;
                this.gsOptions.api.optionsChanged();
            }
            this.gsItems = action.payload.map(element => {
                const item = {
                    id: element.id,
                    cols: element.width,
                    rows: element.height,
                    x: element.positionX,
                    y: element.positionY,
                    type: element.type,
                    name: element.name,
                    isLoading: element.isLoading,
                    icons: _.cloneDeep(this.options.icons)
                };
                return item;
            });
        } else if (action.type === TamDashboardActions.SET_LOADING_STATUS) {
            // home page has onPush, the tile loading status changed, so need detectChanges
            this.actionSubject$.next(action);
        } else if (action.type === TamDashboardActions.ADD_ITEM) {
            const id = UUID.UUID();
            let cols: number, rows: number;
            if (action.payload === TamDashboardTileType.LINE_CHART ||
                action.payload === TamDashboardTileType.PIE_CHART ||
                action.payload === TamDashboardTileType.COLUMN_CHART ||
                action.payload === TamDashboardTileType.BAR_CHART) {
                cols = 2;
                rows = 3;
            } else if (action.payload === TamDashboardTileType.VALUE) {
                cols = 1;
                rows = 1;
            } else if (action.payload === TamDashboardTileType.METADATA) {
                cols = 3;
                rows = 2;
            } else if (action.payload === TamDashboardTileType.GRID) {
                cols = 6;
                rows = 4;
            } else if (action.payload === TamDashboardTileType.NOTES) {
                cols = 3;
                rows = 4;
            } else if (action.payload === TamDashboardTileType.EDITOR) {
                cols = 2;
                rows = 2;
            } else if (action.payload === TamDashboardTileType.MAP) {
                cols = 6;
                rows = 5;
            }
            this.gsItems.push({
                x: 0,
                y: this._getDashboardHeight(this.gsItems),
                cols: cols,
                rows: rows,
                id: id,
                name: '',
                type: action.payload,
                notPreview: true,
                isLoading: action.payload !== 6,
                icons: _.cloneDeep(this.options.icons)
            });
        } else if (action.type === TamDashboardActions.SET_TILE_NAME) {
            this.gsItems.forEach(item => {
                if (item.id === action.id) {
                    item.name = action.payload;
                }
            });
        } else if (action.type === TamDashboardActions.SET_TILE_ACTIVE) {
            this.gsItems.forEach(item => {
                item.active = item.id === action.id;
            });
            /** dashboard edit page, gridster scroll to active gsItem */
            this._scrollToAvtiveGsItem();
            this.actionSubject$.next(action);
        } else if (action.type === TamDashboardActions.SET_TILE_PREVIEW_STATUS) {
            this.gsItems.forEach(item => {
                if (item.id === action.id) {
                    item.notPreview = action.payload;
                }
            });
            this.options.feedbackSubject$.next({ type: TamDashboardActions.SET_TILE_PREVIEW_STATUS_SUCCESS });
        } else if (action.type === TamDashboardActions.SET_TILE_TYPE) {
            this.gsItems.forEach(item => {
                if (item.id === action.id) {
                    if (item.type !== action.payload) {
                        item.type = action.payload;
                    }
                    this.actionSubject$.next(action);
                }
            });
        } else if (action.type === TamDashboardActions.DELETE_ITEM) {
            const index = this.gsItems.findIndex(item => item.id === action.id);
            if (index !== -1) {
                this.gsItems.splice(index, 1);
            }
        } else if (action.type === TamDashboardActions.COPY_ITEM) {
            const id = UUID.UUID();
            this.gsItems.push({
                x: 0,
                y: this._getDashboardHeight(this.gsItems),
                cols: action.payload.cols,
                rows: action.payload.rows,
                id: id,
                name: action.payload.name,
                type: action.payload.type,
                notPreview: false,
                tile: action.tile,
                copyTileId: action.copyTileId,
                icons: _.cloneDeep(action.payload.icons)
            });
        } else {
            this.actionSubject$.next(action);
        }
    }

    private _openDrillDown(data) {
        this.drillDownConfig = Object.assign({}, data);
        this.drillDownConfig.show = true;
        this.gsItems.forEach(item => {
            if (item.id === data.id) {
                this.drillDownConfig['style'] = item;
            }
        });
    }

    private _onSubscribeEvents(event) {
        if (event.type === TamDashboardEvents.OPEN_DRILL_DOWN) {
            this._openDrillDown(event.payload);
        } else if (event.type === TamDashboardEvents.EXPAND_COLLAPSE) {
            this._tileChangeHeight(event.id, event.payload);
        } else {
            this.options.feedbackSubject$.next(event);
        }
    }

    private _scrollToAvtiveGsItem() {
        const activeGsItem = this.gsItems.filter((item) => item.active);
        if (activeGsItem && activeGsItem[0]) {
            let scrollHeight = activeGsItem[0].y * this.gsOptions.fixedRowHeight;
            setTimeout(() => {
                if (scrollHeight > this.gridsterEl['el'].scrollHeight) {
                    scrollHeight = this.gridsterEl['el'].scrollHeight;
                }
                this.gridsterEl['el'].scrollTop = scrollHeight;
            }, 500);
        }
    }

    private _tileChangeHeight(id: string, height: number) {
        const yPushes = [0, 0, 0, 0, 0, 0];
        this.gsItems.sort((first, second) =>
            first.x - second.x + 6 * first.y - 6 * second.y
        );
        for (let i = 0; i < this.gsItems.length; i++) {
            let shift = 0;
            for (let j = this.gsItems[i].x; j < this.gsItems[i].x + this.gsItems[i].cols; j++) {
                if (yPushes[j] > shift) {
                    shift = yPushes[j];
                }
            }
            this.gsItems[i].y += shift;

            if (this.gsItems[i].id === id) {
                const previousRows = this.gsItems[i].rows;
                this.gsItems[i].rows = Math.ceil((height + 10) / 160); // row hight is 150px but there's 10px gap between rows
                const deltaY = this.gsItems[i].rows - previousRows;
                for (let j = 0; j < yPushes.length; j++) {
                    if (j >= this.gsItems[i].x && j < this.gsItems[i].x + this.gsItems[i].cols) {
                        yPushes[j] += deltaY;
                    }
                }
            }
        }
        this.gsOptions.api.optionsChanged();
    }
}


